import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import useEDB from "$hooks/useEDB";
import useAuth from "$hooks/useAuth";
import { STEP } from "../types/edb.types";
import { useEDBFormContext } from "../context/EDBFormContext";
import { EdbStatus } from "$types";

export default function useEDBFormHandlers() {
  const { user } = useAuth();
  const { saveEDB, deleteEDB } = useEDB();
  const { getValues, trigger } = useFormContext();
  const navigate = useNavigate();
  const { showCart, setShowCart, step, setStep, maxStepIndex } =
    useEDBFormContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = useCallback(
    async (isCartCreated = false) => {
      if (isSubmitting) return;

      try {
        setIsSubmitting(true);
        const isValid = isCartCreated || (await trigger());

        if (isValid) {
          const data = getValues();
          data.createdBy = user?.userInfo?.id;
          data.status = isCartCreated
            ? EdbStatus.CART_CREATED
            : EdbStatus.WAITING;
          await saveEDB(data);
        }
      } catch (error) {
        console.error("Error submitting EDB:", error);
        // Handle error here if needed
      } finally {
        setIsSubmitting(false);
      }
    },
    [getValues, saveEDB, trigger, user?.userInfo?.id, isSubmitting]
  );

  const handleDelete = useCallback(
    async (idEdb: string) => {
      if (isSubmitting) return;

      try {
        setIsSubmitting(true);
        await deleteEDB(idEdb);
        navigate("/edbs");
      } catch (error) {
        console.error("Error deleting EDB:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [deleteEDB, navigate, isSubmitting]
  );

  const onCancel = useCallback(() => {
    if (isSubmitting) return;

    if (step === STEP.PRODUCT_STEP && showCart) {
      setShowCart(false);
    } else {
      setStep(step - 1);
    }
  }, [showCart, step, setShowCart, setStep, isSubmitting]);

  const handleNextStep = useCallback(() => {
    if (isSubmitting) return;

    if (step === maxStepIndex) {
      onSubmit(false);
    } else if (step === STEP.PRODUCT_STEP && !showCart) {
      setShowCart(true);
    } else {
      setStep(step + 1);
    }
  }, [
    showCart,
    step,
    maxStepIndex,
    onSubmit,
    setShowCart,
    setStep,
    isSubmitting,
  ]);

  return {
    onSubmit,
    handleDelete,
    onCancel,
    handleNextStep,
    isSubmitting,
  };
}
